import {
  DrawerBody,
  DrawerFooter,
  DrawerContent,
  Drawer,
  DrawerHeader,
  Button,
  Card,
  Chip,
  CardBody,
  CardFooter,
  addToast,
  Radio,
  Spinner,
  Tab,
  Tabs,
} from "@heroui/react";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { LimeRadioGroup } from "@/Components/NextBase/LimeRadioGroup";
import { useAvailableFiscalizationTaxSystems } from "@/hooks/useAvailableFiscalizationTaxSystems";
import { useClientFeature } from "@/hooks/useClientFeature";
import { api } from "@/lib/api-client";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useForm } from "@mantine/form";
import { cn, getLocalizedName, getTranslatedTimeframe } from "@/utils";
import {
  getCompanySplitFormErrors,
  getCompanySplitState,
  toStoredCompanyShareValue,
  type CompanyShareType,
} from "@/utils/subscription-shares";
import {
  LimitationTimeframe,
  PostCustomerSubscription,
  SubscriptionDurationUnit,
  TaxSystem,
} from "@/server-types";
import { useEffect, useRef, useState } from "react";
import { LimeDatePicker } from "@/Components/NextBase/LimeDatePicker";
import { CalendarDate, getLocalTimeZone } from "@internationalized/date";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import LimitationsSection from "@/pages/Dashboard/Subscriptions/components/tabs/LimitationsSection";
import {
  getDefaultTaxPercentage,
  getTaxPercentageItems,
  getTaxPercentageValues,
} from "@/utils/taxPercentages";
import {
  formatCurrency,
  returnCurrencySymbol,
} from "../../../../../../../../shared/utils/utils";
import dayjs from "dayjs";
import { CompanySplitFields } from "./CompanySplitFields";
import { CustomSubscriptionServicesEditor } from "./CustomSubscriptionServicesEditor";
import { Search } from "lucide-react";
import { getFirstUpcomingCoveredAppointment } from "./getFirstUpcomingCoveredAppointment";
import { CustomerSubscriptionLocationsField } from "@/pages/Dashboard/Subscriptions/components/SubscriptionLocationsField";
import { planTermLabel } from "@/pages/Dashboard/Subscriptions/commitmentPlanTerm";
import {
  canSellOneTime,
  commitmentCofundingError,
  commitmentPlanChoiceError,
  getOfferedCommitmentPlans,
  resolveChosenCommitmentPlan,
  saleIsRecurring,
} from "./commitmentPlanChoice";

const STEPS = ["subscription", "variation", "custom", "confirmation"] as const;

type CustomerSubscriptionDrawerProps = {
  isOpen: boolean;
  onOpenChange: () => void;
  customerId: number;
  preselectedSubscriptionId?: number;
  preselectedVariationId?: number;
  onSubmitOverride?: (
    values: CustomerSubscriptionFormValues,
  ) => void | Promise<void>;
  onCreated?: (subscription: PostCustomerSubscription["response"]) => void;
};

type CustomerSubscriptionFormValues = {
  currentStep: (typeof STEPS)[number];
  subscriptionId?: number;
  variationId?: number;
  commitmentPlanId: number | null;
  name: string;
  taxSystem: TaxSystem;
  isRecurring: boolean;
  validFrom: CalendarDate;
  durationValue: number;
  durationUnit: SubscriptionDurationUnit;
  price: number;
  taxPercentage: number;
  autoRenewOnVisitExhaustion: boolean;
  shouldIssueEstimates: boolean;
  sendEstimateViaEmail: boolean;
  notifyCustomer: boolean;
  estimateOrganizationId: string | null;
  markFirstPeriodAsPaid: boolean;
  discountEnabled: boolean;
  discountType: "percentage" | "amount";
  discountAmount: number;
  discountMaxPeriodsEnabled: boolean;
  discountMaxPeriods: number;
  companySplitEnabled: boolean;
  payerCompanyId: number | null;
  companyShareType: CompanyShareType;
  companyShareValue: number;
  companyShouldReceiveEstimatesOnEmail: boolean;
  // `allLocations` means every location the chosen subscription is offered at.
  allLocations: boolean;
  locationIds: number[];
  limitations: {
    timeframe: LimitationTimeframe;
    limitAmount: number;
  }[];
  subscriptionServices: {
    serviceId: number;
    limitations: {
      timeframe: LimitationTimeframe;
      limitAmount: number;
    }[];
    // UI-only helper
    limitEntries?: boolean;
  }[];
};

// Renewing on visit exhaustion needs a period-total limit to exhaust and a
// recurring package to renew into; same rule the subscription editor uses.
function canAutoRenewOnVisitExhaustion(values: CustomerSubscriptionFormValues) {
  return (
    values.isRecurring &&
    values.limitations.some((limitation) => limitation.timeframe === "all")
  );
}

const subscriptionTypeRadioClassNames = {
  base: cn(
    "m-0 inline-flex !w-full !max-w-none cursor-pointer items-center justify-between gap-4 rounded-lg border-2 border-default-100 bg-content1 p-4",
    "flex-row-reverse hover:bg-content2 data-[selected=true]:border-primary",
  ),
};

function CustomerSubscriptionDrawer({
  isOpen,
  onOpenChange,
  customerId,
  preselectedSubscriptionId,
  preselectedVariationId,
  onSubmitOverride,
  onCreated,
}: CustomerSubscriptionDrawerProps) {
  const isPosMode = onSubmitOverride != null;
  const [subscriptionSearchQuery, setSubscriptionSearchQuery] = useState("");
  const wasOpenRef = useRef(isOpen);
  const hydratedPreselectedVariationRef = useRef<string | null>(null);

  const {
    data: subscriptions = [],
    isFetching: isSubscriptionsLoading,
    processedErrorMessage: subscriptionsError,
  } = api.subscription.useGetSubscriptions({
    includeDeleted: false,
    includeInactive: false,
  });

  const { data: servicesData, isFetching: isServicesLoading } =
    api.service.useGetServices({ enabled: true });

  const { data: locationsData, isFetching: isLocationsLoading } =
    api.location.useGetLocationsByClient();

  const { data: currency } = api.values.useGetCurrency({});
  const currencySymbol = currency ? returnCurrencySymbol({ currency }) : "";

  const availableServices = servicesData?.services ?? [];

  const normalizedSearchQuery = subscriptionSearchQuery.trim().toLowerCase();
  const filteredSubscriptions = normalizedSearchQuery
    ? subscriptions.filter((subscription) =>
        [
          subscription.name,
          ...subscription.tags.map((tag) => tag.name),
          ...subscription.subscriptionServices.map(
            (subscriptionService) => subscriptionService.service.name,
          ),
          ...subscription.subscriptionVariations.map(
            (variation) => variation.name,
          ),
        ].some((value) => value.toLowerCase().includes(normalizedSearchQuery)),
      )
    : subscriptions;

  const {
    mutateAsync: createCustomerSubscription,
    processedErrorMessage: createCustomerSubscriptionError,
    isPending: isCreatingSubscription,
  } = api.customer.useCreateCustomerSubscription();
  const { isFeatureEnabled } = useClientFeature();
  const commitmentsEnabled = isFeatureEnabled("subscriptionCommitments");
  const { availableTaxSystems, hasActiveFiscalizations } =
    useAvailableFiscalizationTaxSystems();
  const { data: fiscalizations } =
    api.taxRegister.useGetTaxRegisterFiscalizations({
      enabled: isOpen && isFeatureEnabled("taxRegister"),
    });
  const { data: customer } = api.customer.useGetCustomer({
    customerId,
    enabled: isOpen,
  });

  const { data: upcomingAppointments } =
    api.customer.useGetCustomerAppointments({
      customerId,
      query: { status: "upcoming", pagination: { page: 1, perPage: 50 } },
      enabled: isOpen,
    });

  const { data: subscriptionSettings } =
    api.subscription.useGetSubscriptionSettings();

  const defaultEstimateOrganizationName =
    subscriptionSettings?.defaultOrganizationNameForSubscriptionEstimates ??
    null;
  const defaultEstimateOrganizationPlaceholder = defaultEstimateOrganizationName
    ? t`Privzeta (${defaultEstimateOrganizationName})`
    : t`Privzeta organizacija`;

  // Estimate issuance requires the subscriptions integration; tax-rate configuration only
  // requires an active tax register, so a client can set rates before enabling the integration.
  const canIssueEstimates =
    isFeatureEnabled("taxRegister") &&
    isFeatureEnabled("subscriptionsTaxRegisterIntegration");
  const canNotifyCustomer = isFeatureEnabled(
    "subscriptionNotificationSettings",
  );
  const shouldShowTaxSystemSelect =
    hasActiveFiscalizations && availableTaxSystems.length > 1;

  function isCofundedSale(values: CustomerSubscriptionFormValues) {
    return values.companySplitEnabled && values.payerCompanyId != null;
  }

  function getCommitmentPlanSelection(values: CustomerSubscriptionFormValues) {
    const variation = subscriptions
      .find((subscription) => subscription.id === values.subscriptionId)
      ?.subscriptionVariations.find(
        (subscriptionVariation) =>
          subscriptionVariation.id === values.variationId,
      );
    const offeredPlans = getOfferedCommitmentPlans(variation, {
      commitmentsEnabled,
      isCofunded: isCofundedSale(values),
      isRecurring: values.isRecurring,
    });
    return {
      variation,
      offeredPlans,
      chosenPlan: resolveChosenCommitmentPlan(
        values.commitmentPlanId,
        offeredPlans,
      ),
    };
  }

  // The split is judged on what the first period bills, which a signed term discounts.
  function withSalePrice(values: CustomerSubscriptionFormValues) {
    const { chosenPlan } = getCommitmentPlanSelection(values);
    return chosenPlan
      ? { ...values, price: chosenPlan.priceCents / 100 }
      : values;
  }

  const form = useForm<CustomerSubscriptionFormValues>({
    initialValues: {
      currentStep: "subscription",
      subscriptionId: undefined,
      variationId: undefined,
      commitmentPlanId: null,
      name: "",
      isRecurring: false,
      validFrom: new CalendarDate(
        new Date().getFullYear(),
        new Date().getMonth() + 1,
        new Date().getDate(),
      ),
      durationValue: 0,
      durationUnit: "month",
      price: 0,
      taxSystem: "furs",
      taxPercentage: getDefaultTaxPercentage("furs"),
      autoRenewOnVisitExhaustion: false,
      shouldIssueEstimates: true,
      sendEstimateViaEmail: false,
      notifyCustomer: true,
      estimateOrganizationId: null,
      markFirstPeriodAsPaid: false,
      discountEnabled: false,
      discountType: "percentage",
      discountAmount: 0,
      discountMaxPeriodsEnabled: false,
      discountMaxPeriods: 1,
      companySplitEnabled: false,
      payerCompanyId: null,
      companyShareType: "percentage",
      companyShareValue: 0,
      companyShouldReceiveEstimatesOnEmail: false,
      allLocations: true,
      locationIds: [],
      limitations: [],
      subscriptionServices: [],
    },
    // The POS sells at full price with nowhere to record a split; co-funding is set up on the subscription later.
    validate: {
      locationIds: (value, values) =>
        !values.allLocations && value.length === 0
          ? t`Izberite vsaj eno lokacijo`
          : null,
      commitmentPlanId: (_value, values) => {
        const { variation, chosenPlan, offeredPlans } =
          getCommitmentPlanSelection(values);
        return commitmentPlanChoiceError(variation, chosenPlan, offeredPlans);
      },
      payerCompanyId: (_value, values) => {
        if (isPosMode) return null;
        const { variation } = getCommitmentPlanSelection(values);
        return (
          commitmentCofundingError(variation, {
            commitmentsEnabled,
            isCofunded: isCofundedSale(values),
          }) ??
          getCompanySplitFormErrors(withSalePrice(values)).payerCompanyId ??
          null
        );
      },
      companyShareValue: (_value, values) =>
        isPosMode
          ? null
          : (getCompanySplitFormErrors(withSalePrice(values))
              .companyShareValue ?? null),
    },
  });

  const { isCoFunded, isCompanyFundingEverything } = getCompanySplitState(
    withSalePrice(form.values),
  );

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      form.reset();
      setSubscriptionSearchQuery("");
      hydratedPreselectedVariationRef.current = null;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (
      !isOpen ||
      preselectedSubscriptionId == null ||
      preselectedVariationId == null
    ) {
      return;
    }

    const currentValues = form.getValues();
    if (
      currentValues.subscriptionId === preselectedSubscriptionId &&
      currentValues.variationId === preselectedVariationId &&
      currentValues.currentStep === "confirmation"
    ) {
      return;
    }

    form.setValues({
      ...currentValues,
      subscriptionId: preselectedSubscriptionId,
      variationId: preselectedVariationId,
      sendEstimateViaEmail: false,
      currentStep: "confirmation",
    });
  }, [form, isOpen, preselectedSubscriptionId, preselectedVariationId]);

  useEffect(() => {
    if (!isOpen || preselectedVariationId == null) {
      return;
    }

    const subscriptionWithVariation =
      (preselectedSubscriptionId != null
        ? subscriptions.find(
            (subscription) => subscription.id === preselectedSubscriptionId,
          )
        : undefined) ??
      subscriptions.find((subscription) =>
        subscription.subscriptionVariations.some(
          (variation) => variation.id === preselectedVariationId,
        ),
      );

    const variation = subscriptionWithVariation?.subscriptionVariations.find(
      (subscriptionVariation) =>
        subscriptionVariation.id === preselectedVariationId,
    );

    if (!subscriptionWithVariation || !variation) {
      return;
    }

    const preselectedHydrationKey = `${preselectedSubscriptionId ?? "none"}:${preselectedVariationId}`;
    if (hydratedPreselectedVariationRef.current === preselectedHydrationKey) {
      return;
    }

    const currentValues = form.getValues();

    form.setValues({
      ...currentValues,
      subscriptionId: subscriptionWithVariation.id,
      variationId: preselectedVariationId,
      commitmentPlanId: null,
      durationValue: variation.durationValue,
      durationUnit: variation.durationUnit,
      price: variation.priceCents / 100,
      isRecurring: saleIsRecurring(variation, {
        commitmentsEnabled,
        isCofunded: isCofundedSale(currentValues),
      }),
      limitations: variation.limitations,
      subscriptionServices: subscriptionWithVariation.subscriptionServices,
      autoRenewOnVisitExhaustion: variation.autoRenewOnVisitExhaustion,
      shouldIssueEstimates: true,
      sendEstimateViaEmail: false,
      allLocations: true,
      locationIds: [],
      currentStep: "confirmation",
    });
    hydratedPreselectedVariationRef.current = preselectedHydrationKey;
  }, [
    form,
    isOpen,
    preselectedSubscriptionId,
    preselectedVariationId,
    subscriptions,
  ]);

  const handleSubscriptionChange = (subscriptionId: number) => {
    const subscription = subscriptions.find((s) => s.id === subscriptionId);
    if (!subscription) {
      addToast({
        title: t`Napaka`,
        description: t`Paket ni najden`,
      });
      return;
    }
    form.setValues({
      ...form.values,
      subscriptionId,
      subscriptionServices: subscription.subscriptionServices,
      allLocations: true,
      locationIds: [],
      currentStep: "variation",
    });
  };

  const handleSelectCustomSubscription = () => {
    form.setValues({
      ...form.values,
      subscriptionId: undefined,
      variationId: undefined,
      subscriptionServices: [],
      limitations: [],
      autoRenewOnVisitExhaustion: false,
      allLocations: true,
      locationIds: [],
      durationUnit: "month",
      durationValue:
        form.values.durationValue && form.values.durationValue > 0
          ? form.values.durationValue
          : 1,
      currentStep: "custom",
    });
  };

  const handleVariationChange = (variationId: number) => {
    const subscription = subscriptions.find(
      (s) => s.id === form.values.subscriptionId,
    );
    const variation = subscription?.subscriptionVariations.find(
      (v) => v.id === variationId,
    );

    if (!subscription || !variation) {
      addToast({
        color: "danger",
        title: t`Napaka`,
        description: t`Paket ali variacija ni najdena`,
      });
      return;
    }

    form.setValues({
      ...form.values,
      variationId,
      commitmentPlanId: null,
      durationValue: variation.durationValue,
      durationUnit: variation.durationUnit,
      price: variation.priceCents / 100,
      isRecurring: saleIsRecurring(variation, {
        commitmentsEnabled,
        isCofunded: isCofundedSale(form.values),
      }),
      limitations: variation.limitations,
      autoRenewOnVisitExhaustion: variation.autoRenewOnVisitExhaustion,
      shouldIssueEstimates: true,
      currentStep: "confirmation",
    });
  };

  const firstUpcomingAppointment = getFirstUpcomingCoveredAppointment({
    customerAppointments: upcomingAppointments?.customerAppointments ?? [],
    serviceIds: form.values.subscriptionServices.map(
      (service) => service.serviceId,
    ),
    now: new Date(),
  });

  const firstUpcomingAppointmentLabel = firstUpcomingAppointment
    ? `${firstUpcomingAppointment.date
        .toDate(getLocalTimeZone())
        .toLocaleDateString(i18n.locale, {
          weekday: "short",
          day: "numeric",
          month: "numeric",
          year: "numeric",
        })} (${getLocalizedName(
        firstUpcomingAppointment.customerAppointment.appointment.service
          .nameLocalized,
      )})`
    : "";

  const handleSubmit = async (values: CustomerSubscriptionFormValues) => {
    const normalizedValues = {
      ...values,
      // The switch is hidden when the package can't renew on exhaustion, so a
      // value left over from another package must not be submitted.
      autoRenewOnVisitExhaustion:
        canAutoRenewOnVisitExhaustion(values) &&
        values.autoRenewOnVisitExhaustion,
      shouldIssueEstimates: canIssueEstimates && values.shouldIssueEstimates,
      // The POS invoice covers the first period, so there is no first-period
      // estimate to email.
      sendEstimateViaEmail:
        !isPosMode &&
        canIssueEstimates &&
        values.shouldIssueEstimates &&
        values.sendEstimateViaEmail,
      companySplitEnabled: !isPosMode && values.companySplitEnabled,
    };

    if (onSubmitOverride) {
      if (!normalizedValues.variationId) {
        addToast({
          title: t`Napaka`,
          description: t`Izberite variacijo paketa`,
          color: "danger",
        });
        return;
      }

      const { chosenPlan: commitmentPlan } = getCommitmentPlanSelection(values);
      await onSubmitOverride({
        ...normalizedValues,
        commitmentPlanId: commitmentPlan?.id ?? null,
        // The POS invoice pays the first period, which a term prices at the plan's price.
        price: commitmentPlan
          ? commitmentPlan.priceCents / 100
          : normalizedValues.price,
        validFrom: new CalendarDate(
          normalizedValues.validFrom.year,
          normalizedValues.validFrom.month,
          normalizedValues.validFrom.day,
        ),
      });

      onOpenChange();
      form.reset();
      return;
    }

    if (selectedSubscriptionDetails && !values.subscriptionId) {
      addToast({
        title: t`Napaka`,
        description: t`Izberite paket`,
        color: "danger",
      });
      return;
    }

    try {
      const discountAmount =
        values.discountType === "amount"
          ? Math.round(values.discountAmount * 100)
          : Math.round(values.discountAmount);
      const trimmedName = values.name.trim();
      // Duration, limitations and services are copied from the variation server-side;
      // only a custom (variation-less) sub defines its own plan.
      const customPlan =
        values.variationId == null
          ? {
              durationValue: values.durationValue,
              durationUnit: values.durationUnit,
              limitations: values.limitations,
              subscriptionServices: values.subscriptionServices.map((s) => ({
                serviceId: s.serviceId,
                limitations: s.limitations,
              })),
            }
          : {};
      const { chosenPlan: commitmentPlan } = getCommitmentPlanSelection(values);
      const body = {
        customerId,
        validFrom: values.validFrom.toString(),
        isRecurring: values.isRecurring,
        // A signed term is priced from the catalogue.
        priceCents:
          commitmentPlan == null ? Math.round(values.price * 100) : undefined,
        subscriptionCommitmentPlanId: commitmentPlan?.id,
        // Tax is operator-entered only for custom (variation-less) subs with an active tax
        // register to apply it to; variation-based subs derive it server-side, and without a
        // tax register there is no rate to store.
        taxPercentage:
          values.variationId == null && hasActiveFiscalizations
            ? values.taxPercentage
            : null,
        ...customPlan,
        variationId: values.variationId,
        name:
          values.subscriptionId == null && trimmedName.length > 0
            ? trimmedName
            : undefined,
        autoRenewOnVisitExhaustion: normalizedValues.autoRenewOnVisitExhaustion,
        shouldIssueEstimates: normalizedValues.shouldIssueEstimates,
        sendEstimateViaEmail: normalizedValues.sendEstimateViaEmail,
        notifyCustomer: values.notifyCustomer,
        estimateOrganizationId:
          normalizedValues.shouldIssueEstimates &&
          values.estimateOrganizationId != null &&
          values.estimateOrganizationId.length > 0
            ? values.estimateOrganizationId
            : undefined,
        // A period the company funds whole has no member payment to record.
        markFirstPeriodAsPaid:
          values.markFirstPeriodAsPaid && !isCompanyFundingEverything,
        ...(values.companySplitEnabled && values.payerCompanyId != null
          ? {
              payerCompanyId: values.payerCompanyId,
              companyShareType: values.companyShareType,
              companyShareValue: toStoredCompanyShareValue(
                values.companyShareType,
                values.companyShareValue,
              ),
              companyShouldReceiveEstimatesOnEmail:
                values.companyShouldReceiveEstimatesOnEmail,
            }
          : {}),
        discount: values.discountEnabled
          ? {
              discountType: values.discountType,
              discountAmount,
              ...(values.discountMaxPeriodsEnabled && {
                maxPeriods: Math.round(values.discountMaxPeriods),
              }),
            }
          : undefined,
        allLocations: values.allLocations,
        locationIds: values.allLocations ? undefined : values.locationIds,
      };

      const createdSubscription = await createCustomerSubscription({ body });

      addToast({
        title: t`Uspešno`,
        description: t`Paket je bil uspešno dodan stranki`,
        color: "success",
      });

      onCreated?.(createdSubscription);
      onOpenChange();
      form.reset();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: createCustomerSubscriptionError,
        color: "danger",
      });
    }
  };

  const selectedSubscriptionDetails = subscriptions.find(
    (s) => s.id === form.values.subscriptionId,
  );

  const {
    variation: selectedVariation,
    offeredPlans: offeredCommitmentPlans,
    chosenPlan: chosenCommitmentPlan,
  } = getCommitmentPlanSelection(form.values);
  const oneTimeAllowed = canSellOneTime(selectedVariation, {
    commitmentsEnabled,
    isCofunded: isCofundedSale(form.values),
  });
  const commitmentPlanPickerValue = (() => {
    if (chosenCommitmentPlan) {
      return String(chosenCommitmentPlan.id);
    }
    if (selectedVariation?.requiresCommitment) {
      return null;
    }
    return "none";
  })();

  function formatPrice(priceCents: number): string {
    return formatCurrency({
      amount: priceCents / 100,
      currency: currency ?? "",
      locale: i18n.locale,
    });
  }

  const offeredLocations = (locationsData?.locations ?? []).filter(
    (location) =>
      selectedSubscriptionDetails == null ||
      selectedSubscriptionDetails.allLocations ||
      selectedSubscriptionDetails.locationIds.includes(location.locationId),
  );

  const currentStepIndex = STEPS.indexOf(form.values.currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === STEPS.length - 1;

  const goToNextStep = () => {
    if (form.values.currentStep === "subscription") {
      if (form.values.subscriptionId) {
        form.setFieldValue("currentStep", "variation");
      }
      return;
    }

    if (form.values.currentStep === "variation") {
      form.setFieldValue("currentStep", "confirmation");
      return;
    }

    if (form.values.currentStep === "custom") {
      form.setFieldValue("currentStep", "confirmation");
      return;
    }

    if (!isLastStep) {
      form.setFieldValue("currentStep", STEPS[currentStepIndex + 1]);
    }
  };

  const goToPreviousStep = () => {
    if (form.values.currentStep === "custom") {
      form.setFieldValue("currentStep", "subscription");
      return;
    }

    if (form.values.currentStep === "confirmation") {
      if (form.values.subscriptionId) {
        form.setFieldValue("currentStep", "variation");
      } else {
        form.setFieldValue("currentStep", "custom");
      }
      return;
    }

    if (!isFirstStep) {
      form.setFieldValue("currentStep", STEPS[currentStepIndex - 1]);
    }
  };

  useEffect(() => {
    if (availableTaxSystems.length === 0) {
      return;
    }

    const taxSystem = availableTaxSystems.includes(form.values.taxSystem)
      ? form.values.taxSystem
      : availableTaxSystems[0];

    if (taxSystem !== form.values.taxSystem) {
      form.setFieldValue("taxSystem", taxSystem);
      return;
    }

    // Variation-based subs derive tax from the subscription server-side, so the
    // client never shows or stores a rate for them — only custom subs need this.
    const isVariationBasedSub =
      selectedSubscriptionDetails?.subscriptionVariations.some(
        (variation) => variation.id === form.values.variationId,
      ) ?? false;

    if (isVariationBasedSub) {
      return;
    }

    const availableTaxPercentages = getTaxPercentageValues(taxSystem);

    if (!availableTaxPercentages.includes(form.values.taxPercentage)) {
      form.setFieldValue("taxPercentage", getDefaultTaxPercentage(taxSystem));
    }
  }, [
    availableTaxSystems,
    form,
    form.values.taxPercentage,
    form.values.taxSystem,
    form.values.variationId,
    selectedSubscriptionDetails,
  ]);

  const handleTaxSystemChange = (value: string) => {
    // Only fires from the custom (variation-less) step's tax-system select, so
    // there is never a variation here — the rate is always operator-chosen.
    const taxSystem = value as TaxSystem;
    form.setFieldValue("taxSystem", taxSystem);

    const availableTaxPercentages = getTaxPercentageValues(taxSystem);
    if (!availableTaxPercentages.includes(form.values.taxPercentage)) {
      form.setFieldValue("taxPercentage", getDefaultTaxPercentage(taxSystem));
    }
  };

  const renderStepContent = () => {
    if (isSubscriptionsLoading) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <Spinner />
        </div>
      );
    }

    switch (form.values.currentStep) {
      case "subscription":
        return (
          <div className="flex flex-col gap-4">
            <LimeInput
              isClearable
              variant="bordered"
              className="w-full"
              placeholder={t`Iskanje paketov...`}
              startContent={<Search className="text-gray-400" size={16} />}
              value={subscriptionSearchQuery}
              data-identifier="customer-subscription-search-input"
              onClear={() => {
                setSubscriptionSearchQuery("");
              }}
              onValueChange={setSubscriptionSearchQuery}
            />

            <Card
              isPressable
              isHoverable
              shadow="none"
              className="hover:border-primary w-full cursor-pointer border-2 border-dashed border-gray-300"
              onPress={handleSelectCustomSubscription}
              data-identifier="custom-subscription-card"
            >
              <CardBody>
                <h3 className="text-xl font-medium">
                  <Trans>Paket po meri</Trans>
                </h3>
                <p className="text-sm text-gray-600">
                  <Trans>Ustvari paket po meri prilagojen za stranko</Trans>
                </p>
              </CardBody>
            </Card>

            {filteredSubscriptions.map((subscription) => (
              <Card
                key={subscription.id}
                isPressable
                isHoverable
                shadow="none"
                className={`w-full cursor-pointer border-2 ${
                  form.values.subscriptionId === subscription.id
                    ? "border-primary"
                    : "border-[#d8d8d8]"
                }`}
                onPress={() => handleSubscriptionChange(subscription.id)}
                data-identifier="subscription-option"
              >
                <CardBody>
                  <h3 className="text-xl font-medium">{subscription.name}</h3>
                  <p className="text-sm text-gray-600">
                    {subscription.description}
                  </p>
                  <div className="mt-3 space-y-3">
                    {subscription.subscriptionVariations.length > 0 && (
                      <div className="flex flex-col gap-0.5">
                        {subscription.subscriptionVariations.map(
                          (variation) => (
                            <div
                              key={variation.id}
                              className="flex items-baseline gap-1.5  text-xs leading-tight"
                            >
                              <span className="truncate">
                                {variation.name}
                                {" - "}
                                <span className="font-medium">
                                  {formatCurrency({
                                    amount: variation.priceCents / 100,
                                    currency: currency ?? "",
                                    locale: i18n.locale,
                                  })}
                                </span>
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    )}
                    <div>
                      <p className="mb-2 text-xs text-gray-600">
                        <Trans>Vključene storitve</Trans>:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {subscription.subscriptionServices.map((service) => (
                          <Chip radius="sm" key={service.serviceId} size="sm">
                            {service.service.name}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardBody>
                {subscription.tags.length > 0 ? (
                  <CardFooter>
                    <div className="flex flex-wrap gap-2">
                      {subscription.tags.map((tag) => (
                        <Chip radius="sm" key={tag.id} size="sm">
                          {tag.name}
                        </Chip>
                      ))}
                    </div>
                  </CardFooter>
                ) : null}
              </Card>
            ))}

            {normalizedSearchQuery !== "" &&
              filteredSubscriptions.length === 0 && (
                <p className="py-4 text-center text-gray-600 text-sm">
                  <Trans>Ni paketov, ki bi ustrezali iskanju</Trans>
                </p>
              )}
          </div>
        );

      case "custom":
        return (
          <div className="flex flex-col gap-6">
            <Card shadow="sm">
              <div className="space-y-4 p-4">
                <LimeInput
                  data-identifier="custom-subscription-name-input"
                  label={t`Ime paketa`}
                  labelPlacement="outside"
                  placeholder={t`Paket po meri`}
                  maxLength={120}
                  {...form.getInputProps("name")}
                />
                <div
                  className={cn("flex flex-col gap-4 md:flex-row", {
                    "md:flex-col": shouldShowTaxSystemSelect,
                  })}
                >
                  <div className="flex-1">
                    <LimeNumberInput
                      data-identifier="custom-subscription-price-input"
                      label={t`Cena`}
                      labelPlacement="outside"
                      minValue={0}
                      step={0.01}
                      endContent={currencySymbol}
                      {...form.getInputProps("price")}
                    />
                  </div>
                  {shouldShowTaxSystemSelect && (
                    <div className="flex-1">
                      <LimeSelect
                        data-identifier="subscription-tax-system-dropdown"
                        label={t`Davčni sistem`}
                        labelPlacement="outside"
                        value={form.values.taxSystem}
                        onSingleItemChange={handleTaxSystemChange}
                        items={availableTaxSystems.map((taxSystem) => ({
                          key: taxSystem,
                          label: taxSystem.toUpperCase(),
                        }))}
                      />
                    </div>
                  )}
                  {hasActiveFiscalizations && (
                    <div className="flex-1">
                      <LimeSelect
                        data-identifier={"tax-percentage-dropdown"}
                        label={t`Davčna stopnja`}
                        labelPlacement="outside"
                        value={String(form.values.taxPercentage)}
                        onSingleItemChange={(value) =>
                          form.setFieldValue("taxPercentage", parseFloat(value))
                        }
                        items={getTaxPercentageItems(form.values.taxSystem)}
                      />
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-4 md:flex-row">
                  <div className="flex-1">
                    <LimeNumberInput
                      data-identifier="custom-subscription-duration-input"
                      label={t`Trajanje`}
                      labelPlacement="outside"
                      minValue={1}
                      step={1}
                      {...form.getInputProps("durationValue")}
                    />
                  </div>
                  <div className="flex-1">
                    <LimeSelect
                      data-identifier="custom-subscription-duration-unit"
                      label={t`Enota trajanja`}
                      labelPlacement="outside"
                      items={[
                        { key: "day", label: t`Dan` },
                        { key: "week", label: t`Teden` },
                        { key: "month", label: t`Mesec` },
                      ]}
                      {...form.getInputProps("durationUnit")}
                    />
                  </div>
                </div>
                <LimeRadioGroup
                  className="w-full"
                  error={form.errors.isRecurring}
                  size="sm"
                  orientation="vertical"
                  value={
                    form.getValues().isRecurring ? "recurring" : "one-time"
                  }
                  onValueChange={(value) =>
                    form.setFieldValue("isRecurring", value === "recurring")
                  }
                >
                  <Radio
                    value="recurring"
                    classNames={subscriptionTypeRadioClassNames}
                  >
                    <div className="flex flex-col">
                      <span>
                        <Trans>Ponavljajoči paket</Trans>
                      </span>
                      <span className="text-sm text-gray-500">
                        <Trans>
                          Paket se samodejno obnovi po izteku obdobja
                        </Trans>
                      </span>
                    </div>
                  </Radio>
                  <Radio
                    value="one-time"
                    classNames={subscriptionTypeRadioClassNames}
                  >
                    <div className="flex flex-col">
                      <span>
                        <Trans>Enkratni paket</Trans>
                      </span>
                      <span className="text-sm text-gray-500">
                        <Trans>Paket se ne obnovi samodejno</Trans>
                      </span>
                    </div>
                  </Radio>
                </LimeRadioGroup>
              </div>
            </Card>

            <Card shadow="none">
              <div className="space-y-4 p-4">
                <h3 className="text-lg font-medium">
                  <Trans>Omejitve paketa</Trans>
                </h3>
                <LimitationsSection
                  parentIndex={0}
                  limitations={form.values.limitations}
                  onAddLimitation={() => {
                    form.setFieldValue("limitations", [
                      ...form.values.limitations,
                      { timeframe: "all", limitAmount: 1 },
                    ]);
                  }}
                  onRemoveLimitation={(_, limitationIndex) => {
                    const lims = [...form.values.limitations];
                    lims.splice(limitationIndex, 1);
                    form.setFieldValue("limitations", lims);
                  }}
                  onUpdateLimitation={(_, limitationIndex, field, value) => {
                    form.setFieldValue(
                      `limitations.${limitationIndex}.${field}`,
                      value,
                    );
                  }}
                />
                {canAutoRenewOnVisitExhaustion(form.values) && (
                  <div className="flex items-center gap-2">
                    <LimeSwitch
                      {...form.getInputProps("autoRenewOnVisitExhaustion", {
                        type: "checkbox",
                      })}
                      data-identifier="custom-subscription-auto-renew-on-visit-exhaustion"
                      size="sm"
                      isCompact
                      title={t`Začni novo obdobje paketa, ko so obiski porabljeni`}
                    />
                  </div>
                )}
              </div>
            </Card>

            <Card shadow="none">
              <div className="p-4">
                <CustomSubscriptionServicesEditor
                  heading={
                    <h3 className="text-lg font-medium">
                      <Trans>Storitve</Trans>
                    </h3>
                  }
                  services={form.values.subscriptionServices}
                  onChange={(services) =>
                    form.setFieldValue("subscriptionServices", services)
                  }
                  availableServices={availableServices}
                  isLoading={isServicesLoading}
                />
              </div>
            </Card>
          </div>
        );

      case "variation":
        return (
          <div className="flex flex-col gap-3">
            {selectedSubscriptionDetails?.subscriptionVariations.map(
              (variation) => (
                <Card
                  key={variation.id}
                  isPressable
                  shadow="none"
                  isHoverable
                  className={`w-full cursor-pointer border-1 ${
                    form.values.variationId === variation.id
                      ? "border-primary"
                      : "border-[#d8d8d8]"
                  }`}
                  onPress={() => handleVariationChange(variation.id)}
                  data-identifier="subscription-variation-option"
                >
                  <CardBody className="flex flex-col items-start gap-2 p-4 text-left">
                    <div className="flex w-full items-baseline justify-between gap-3">
                      <h3 className="min-w-0 font-medium text-medium">
                        {variation.name}
                      </h3>
                      <p className="shrink-0 font-semibold text-medium">
                        {formatCurrency({
                          amount: variation.priceCents / 100,
                          currency: currency ?? "",
                          locale: i18n.locale,
                        })}
                      </p>
                    </div>

                    <div className="flex flex-col gap-1 text-default-500 text-small">
                      <p>
                        {t`Trajanje`}:{" "}
                        <span className="text-foreground">
                          {variation.durationValue}{" "}
                          {getTranslatedTimeframe(variation.durationUnit)}
                        </span>
                      </p>
                      {variation.limitations.length > 0 && (
                        <p>
                          {t`Omejitve`}:{" "}
                          <span className="text-foreground">
                            {variation.limitations
                              .map(
                                (limitation) =>
                                  `${limitation.limitAmount}/${getTranslatedTimeframe(limitation.timeframe)}`,
                              )
                              .join(", ")}
                          </span>
                        </p>
                      )}
                    </div>

                    {variation.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {variation.tags.map((tag) => (
                          <Chip
                            radius="sm"
                            key={tag.id}
                            size="sm"
                            variant="bordered"
                          >
                            {tag.name}
                          </Chip>
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              ),
            )}
          </div>
        );

      case "confirmation":
        return (
          <div className="space-y-6">
            <div className="space-y-4 p-4">
              <div>
                <div className="space-y-3 px-1 py-1 text-sm">
                  <div className="border-default-300 flex items-center justify-between border-b border-dashed pb-3">
                    <span className="text-default-500">{t`Paket`}</span>
                    <span className="text-foreground font-semibold">
                      {selectedSubscriptionDetails
                        ? selectedSubscriptionDetails.name
                        : t`Po meri`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-default-500">{t`Trajanje`}</span>
                    <span className="text-foreground">
                      {form.getValues().durationValue}{" "}
                      {getTranslatedTimeframe(form.getValues().durationUnit)}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-default-500">{t`Omejitve`}</span>
                    <span className="text-foreground max-w-[60%] text-right">
                      {renderLimitationsList(form.getValues().limitations)}
                    </span>
                  </div>
                  {form.values.subscriptionServices.some(
                    (service) => service.limitations.length > 0,
                  ) && (
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-default-500">
                        <Trans>Omejitve storitev</Trans>
                      </span>
                      <div className="text-foreground max-w-[60%] space-y-1 text-right">
                        {form.values.subscriptionServices.map((service, i) => {
                          if (service.limitations.length === 0) {
                            return null;
                          }

                          const availableService = availableServices.find(
                            (available) =>
                              available.serviceId === service.serviceId,
                          );

                          return (
                            <p key={`${service.serviceId}-${i}`}>
                              <span className="font-medium">
                                {availableService
                                  ? availableService.name
                                  : t`Storitev #${service.serviceId}`}
                                :{" "}
                              </span>
                              {renderLimitationsList(service.limitations)}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <CustomerSubscriptionLocationsField
                form={form}
                locations={offeredLocations}
                isLoading={isLocationsLoading}
              />

              <div className="space-y-2">
                <LimeDatePicker
                  labelPlacement="outside"
                  label={t`Začetni datum`}
                  {...form.getInputProps("validFrom")}
                  onChange={(date) => {
                    if (date) {
                      form.setFieldValue("validFrom", date as CalendarDate);
                    }
                  }}
                  hideTimeZone
                />
                {firstUpcomingAppointment &&
                  firstUpcomingAppointment.date.compare(
                    form.values.validFrom,
                  ) !== 0 && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-default-500 text-sm">
                        {t`Prvi termin: ${firstUpcomingAppointmentLabel}`}
                      </span>
                      <Button
                        size="sm"
                        variant="light"
                        color="primary"
                        data-identifier="subscription-start-first-appointment"
                        onPress={() =>
                          form.setFieldValue(
                            "validFrom",
                            firstUpcomingAppointment.date,
                          )
                        }
                      >
                        <Trans>Uporabi</Trans>
                      </Button>
                    </div>
                  )}
              </div>

              <LimeRadioGroup
                className="mb-2 w-full"
                error={form.errors.isRecurring}
                size="sm"
                orientation="vertical"
                value={form.getValues().isRecurring ? "recurring" : "one-time"}
                onValueChange={(value) =>
                  form.setFieldValue("isRecurring", value === "recurring")
                }
              >
                <Radio
                  value="recurring"
                  classNames={subscriptionTypeRadioClassNames}
                >
                  <div className="flex flex-col">
                    <span>
                      <Trans>Ponavljajoči paket</Trans>
                    </span>
                    <span className="text-sm text-gray-500">
                      <Trans>Paket se samodejno obnovi po izteku obdobja</Trans>
                    </span>
                  </div>
                </Radio>
                <Radio
                  value="one-time"
                  isDisabled={!oneTimeAllowed}
                  classNames={subscriptionTypeRadioClassNames}
                >
                  <div className="flex flex-col">
                    <span>
                      <Trans>Enkratni paket</Trans>
                    </span>
                    <span className="text-sm text-gray-500">
                      {oneTimeAllowed ? (
                        <Trans>Paket se ne obnovi samodejno</Trans>
                      ) : (
                        <Trans>Ta variacija se prodaja samo z vezavo</Trans>
                      )}
                    </span>
                  </div>
                </Radio>
              </LimeRadioGroup>

              {selectedVariation && offeredCommitmentPlans.length > 0 && (
                <LimeRadioGroup
                  data-identifier="commitment-plan-picker"
                  className="w-full"
                  label={t`Vezava`}
                  error={form.errors.commitmentPlanId}
                  size="sm"
                  orientation="vertical"
                  value={commitmentPlanPickerValue}
                  onValueChange={(value) =>
                    form.setFieldValue(
                      "commitmentPlanId",
                      value === "none" ? null : Number(value),
                    )
                  }
                >
                  {!selectedVariation.requiresCommitment && (
                    <Radio
                      value="none"
                      data-identifier="commitment-plan-option-none"
                      classNames={subscriptionTypeRadioClassNames}
                    >
                      <div className="flex flex-col">
                        <span>
                          <Trans>Brez vezave</Trans>
                        </span>
                        <span className="text-sm text-gray-500">
                          {t`${formatPrice(selectedVariation.priceCents)} na obdobje`}
                        </span>
                      </div>
                    </Radio>
                  )}
                  {offeredCommitmentPlans.map((plan) => (
                    <Radio
                      key={plan.id}
                      value={String(plan.id)}
                      data-identifier={`commitment-plan-option-${plan.id}`}
                      classNames={subscriptionTypeRadioClassNames}
                    >
                      <div className="flex flex-col">
                        <span>
                          {t`Vezava ${planTermLabel(
                            plan.periods,
                            selectedVariation.durationValue,
                            selectedVariation.durationUnit,
                          )}`}
                        </span>
                        <span className="text-sm text-gray-500">
                          {t`${formatPrice(plan.priceCents)} na obdobje`}
                        </span>
                      </div>
                    </Radio>
                  ))}
                </LimeRadioGroup>
              )}

              {canAutoRenewOnVisitExhaustion(form.values) && (
                <div className="flex items-center gap-2">
                  <LimeSwitch
                    {...form.getInputProps("autoRenewOnVisitExhaustion", {
                      type: "checkbox",
                    })}
                    data-identifier="subscription-auto-renew-on-visit-exhaustion"
                    size="sm"
                    isCompact
                    title={t`Začni novo obdobje, ko so obiski porabljeni`}
                  />
                </div>
              )}

              {!isPosMode && canNotifyCustomer && (
                <div className="flex items-center gap-2">
                  <LimeSwitch
                    {...form.getInputProps("notifyCustomer", {
                      type: "checkbox",
                    })}
                    data-identifier="subscription-notify-customer"
                    size="sm"
                    isCompact
                    title={t`Obvesti stranko`}
                    description={t`Stranka prejme obvestilo o dodeljenem paketu.`}
                  />
                </div>
              )}

              {canIssueEstimates && (
                <div className="flex items-center gap-2">
                  <LimeSwitch
                    {...form.getInputProps("shouldIssueEstimates", {
                      type: "checkbox",
                    })}
                    data-identifier="subscription-should-issue-estimates"
                    size="sm"
                    isCompact
                    title={t`Izdajaj predračune za ta paket`}
                    description={
                      isPosMode
                        ? t`Predračuni se izdajajo za prihodnja obdobja; prvo obdobje pokriva ta račun.`
                        : undefined
                    }
                  />
                </div>
              )}

              {canIssueEstimates &&
                !isPosMode &&
                form.values.shouldIssueEstimates && (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <LimeSwitch
                        {...form.getInputProps("sendEstimateViaEmail", {
                          type: "checkbox",
                        })}
                        size="sm"
                        isCompact
                        title={
                          isCoFunded
                            ? t`Pošlji predračun za strankin del prvega obdobja po e-pošti`
                            : t`Pošlji predračun za prvo obdobje stranki po e-pošti`
                        }
                      />
                    </div>
                    {isCompanyFundingEverything ? (
                      <p className="mt-1 flex items-center gap-1 text-sm text-orange-600">
                        {t`Podjetje krije celoten paket, zato stranka ne prejme predračuna.`}
                      </p>
                    ) : (
                      !customer?.email && (
                        <p className="mt-1 flex items-center gap-1 text-sm text-orange-600">
                          {t`Stranka ne bo prejela predračuna na e-pošto, dokler nima vnesenega veljavnega e-poštnega naslova.`}
                        </p>
                      )
                    )}
                  </div>
                )}

              {canIssueEstimates && form.values.shouldIssueEstimates && (
                <LimeSelect
                  label={t`Organizacija za predračune`}
                  labelPlacement="outside"
                  placeholder={defaultEstimateOrganizationPlaceholder}
                  description={t`Če ni izbrana, se uporabi privzeta organizacija iz nastavitev paketov.`}
                  selectionMode="single"
                  disallowEmptySelection={false}
                  items={(fiscalizations?.fiscalizations ?? []).map(
                    (fiscalization) => ({
                      key: fiscalization.organizationId,
                      label: `${fiscalization.organizationName!} (${fiscalization.taxSystem.toUpperCase()})`,
                    }),
                  )}
                  {...form.getInputProps("estimateOrganizationId")}
                />
              )}

              {!selectedSubscriptionDetails &&
                form.values.subscriptionServices.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-md font-medium">
                      <Trans>Storitve</Trans>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {form.values.subscriptionServices.map((s, i) => {
                        const svc = availableServices.find(
                          (x) => x.serviceId === s.serviceId,
                        );
                        return (
                          <Chip
                            key={`${s.serviceId}-${i}`}
                            radius="sm"
                            size="sm"
                          >
                            {svc ? svc.name : t`Storitev #${s.serviceId}`}
                          </Chip>
                        );
                      })}
                    </div>
                  </div>
                )}

              <div className="space-y-4 pt-3">
                <div className="flex flex-col gap-4 md:flex-row">
                  <div className="flex-1">
                    <LimeNumberInput
                      data-identifier="subscription-price-input"
                      label={t`Cena`}
                      labelPlacement="outside"
                      minValue={0}
                      step={0.01}
                      endContent={currencySymbol}
                      {...form.getInputProps("price")}
                      {...(chosenCommitmentPlan && {
                        value: chosenCommitmentPlan.priceCents / 100,
                        isDisabled: true,
                        description: t`Cena je določena z vezavo in se med vezavo ne spreminja.`,
                      })}
                    />
                  </div>
                </div>
              </div>

              {!isPosMode && !isCompanyFundingEverything && (
                <div className="flex items-center gap-2">
                  <LimeSwitch
                    {...form.getInputProps("markFirstPeriodAsPaid", {
                      type: "checkbox",
                    })}
                    data-identifier="subscription-mark-first-period-paid"
                    size="sm"
                    isCompact
                    title={t`Označi prvo obdobje kot plačano`}
                  />
                </div>
              )}

              <LimeSwitch
                {...form.getInputProps("discountEnabled", {
                  type: "checkbox",
                })}
                size="sm"
                isCompact
                title={t`Uporabi popust`}
              />

              {form.values.discountEnabled && (
                <div className="flex flex-col gap-4">
                  <div className="flex justify-center">
                    <Tabs
                      radius="full"
                      selectedKey={form.getValues().discountType}
                      onSelectionChange={(type) =>
                        form.setFieldValue(
                          "discountType",
                          type as "percentage" | "amount",
                        )
                      }
                      disableAnimation
                    >
                      <Tab key="amount" title={currencySymbol} />
                      <Tab key="percentage" title="%" />
                    </Tabs>
                  </div>

                  <LimeInput
                    data-identifier="subscription-discount-amount"
                    placeholder="0"
                    type="number"
                    min={form.values.discountType === "percentage" ? 1 : 0.01}
                    max={
                      form.values.discountType === "percentage"
                        ? 100
                        : undefined
                    }
                    step={form.values.discountType === "percentage" ? 1 : 0.01}
                    {...form.getInputProps("discountAmount")}
                    isRequired
                  />

                  <div className="flex items-center gap-2">
                    <LimeSwitch
                      {...form.getInputProps("discountMaxPeriodsEnabled", {
                        type: "checkbox",
                      })}
                      size="sm"
                      isCompact
                      title={t`Omeji število obdobij s popustom`}
                    />
                  </div>

                  {form.values.discountMaxPeriodsEnabled && (
                    <LimeNumberInput
                      data-identifier="subscription-discount-max-periods"
                      label={t`Število obdobij`}
                      labelPlacement="outside"
                      minValue={1}
                      step={1}
                      {...form.getInputProps("discountMaxPeriods")}
                    />
                  )}
                </div>
              )}

              {!isPosMode && chosenCommitmentPlan == null && (
                <CompanySplitFields
                  issuesEstimates={
                    canIssueEstimates && form.values.shouldIssueEstimates
                  }
                  isEnabled={form.values.companySplitEnabled}
                  onEnabledChange={(isEnabled) =>
                    form.setFieldValue("companySplitEnabled", isEnabled)
                  }
                  payerCompanyId={form.values.payerCompanyId}
                  onPayerCompanyIdChange={(payerCompanyId) =>
                    form.setFieldValue("payerCompanyId", payerCompanyId)
                  }
                  shareType={form.values.companyShareType}
                  onShareTypeChange={(shareType) =>
                    form.setFieldValue("companyShareType", shareType)
                  }
                  shareValue={form.values.companyShareValue}
                  onShareValueChange={(shareValue) =>
                    form.setFieldValue("companyShareValue", shareValue)
                  }
                  currencySymbol={currencySymbol}
                  companyReceivesEstimates={
                    form.values.companyShouldReceiveEstimatesOnEmail
                  }
                  onCompanyReceivesEstimatesChange={(
                    companyReceivesEstimates,
                  ) =>
                    form.setFieldValue(
                      "companyShouldReceiveEstimatesOnEmail",
                      companyReceivesEstimates,
                    )
                  }
                  companyFundsEverything={isCompanyFundingEverything}
                  companyError={form.errors.payerCompanyId}
                  valueError={form.errors.companyShareValue}
                />
              )}
            </div>
          </div>
        );
    }
  };

  const getStepTitle = () => {
    switch (form.values.currentStep) {
      case "subscription":
        return t`Izberi paket`;
      case "variation":
        return t`Izberi variacijo`;
      case "custom":
        return t`Paket po meri`;
      case "confirmation":
        return t`Pregled paketa`;
    }
  };

  return (
    <Drawer isOpen={isOpen} onOpenChange={onOpenChange}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <DrawerContent>
          {(onClose) => (
            <>
              <DrawerHeader>
                <div className="flex flex-col">
                  <Trans>Dodaj paket</Trans>
                  <p className="text-sm font-normal text-gray-500">
                    {getStepTitle()}
                  </p>
                </div>
              </DrawerHeader>

              <DrawerBody>
                <LimeAlert
                  color="danger"
                  message={subscriptionsError}
                  className="mb-4"
                />
                {renderStepContent()}
              </DrawerBody>

              <DrawerFooter>
                <Button
                  type="button"
                  onPress={isFirstStep ? onClose : goToPreviousStep}
                >
                  {isFirstStep ? t`Prekliči` : t`Nazaj`}
                </Button>
                <Button
                  key={form.values.currentStep}
                  type={isLastStep ? "submit" : "button"}
                  color="primary"
                  onPress={goToNextStep}
                  data-identifier="add-customer-subscription-submit-button"
                  isLoading={
                    isLastStep && !onSubmitOverride && isCreatingSubscription
                  }
                  isDisabled={
                    (form.values.currentStep === "subscription" &&
                      !form.values.subscriptionId) ||
                    (form.values.currentStep === "variation" &&
                      !form.values.variationId) ||
                    (form.values.currentStep === "custom" &&
                      form.values.durationValue <= 0) ||
                    (form.values.currentStep === "confirmation" &&
                      dayjs(form.values.validFrom.toDate(getLocalTimeZone()))
                        .startOf("day")
                        .add(
                          form.values.durationValue,
                          form.values.durationUnit,
                        )
                        .subtract(1, "day")
                        .endOf("day")
                        .isBefore(dayjs().startOf("day")))
                  }
                >
                  {isLastStep
                    ? onSubmitOverride
                      ? t`Dodeli paket`
                      : t`Dodaj paket`
                    : t`Nadaljuj`}
                </Button>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </form>
    </Drawer>
  );
}

function renderLimitationsList(
  limitations: {
    timeframe: LimitationTimeframe;
    limitAmount: number;
  }[],
) {
  if (limitations.length === 0) {
    return <Trans>Brez omejitev</Trans>;
  }

  return limitations.map((limitation, index) => (
    <span key={`${limitation.timeframe}-${index}`}>
      {limitation.limitAmount}/{getTranslatedTimeframe(limitation.timeframe)}
      {index < limitations.length - 1 ? ", " : ""}
    </span>
  ));
}

export { CustomerSubscriptionDrawer };
